import { format, fromUnixTime, getUnixTime } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { BookingPost } from "../../components/BookingPost";
import { CTA } from "../../components/CTA";
import { DateRangePicker } from "../../components/DateRangePicker";
import { Dropdown } from "../../components/Dropdown";
import { Flex } from "../../components/Flex";
import { H1, H3 } from "../../components/Heading";
import { FakeInput } from "../../components/Input";
import Loading from "../../components/Loading";
import { StatsCard } from "../../components/StatsCard";
import { Text } from "../../components/Text";
import { TextLink } from "../../components/TextLink";
import { ContentIcon } from "../../components/icons/ContentIcon";
import { DownloadIcon } from "../../components/icons/DownloadIcon";
import { ListingsIcon } from "../../components/icons/ListingsIcon";
import { LocationsIcon } from "../../components/icons/LocationsIcon";
import {
  LARGE_DESKTOP_BREAKPOINT,
  MEDIUM_DESKTOP_BREAKPOINT,
  MOBILE_BREAKPOINT,
  SMALL_DESKTOP_BREAKPOINT,
} from "../../config";
import {
  BookingPostType,
  BookingPostsSortBy,
  ReportMetricsQuery,
  useBookingsMetricsQuery,
  useBrandGroupListingsQuery,
  useBrandGroupLocationsQuery,
  useBrandListingsQuery,
  useBrandLocationsQuery,
  useGetBrandSocialsQuery,
  useReportBookingPostsQuery,
  useReportMetricsQuery,
} from "../../graphql/generated";
import useGqlClient from "../../hooks/useGqlClient";
import { useWindowSize } from "../../hooks/useWindowSize";
import { authSelectors } from "../../store/auth/selector";
import { styled } from "../../styles";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  max-width: ${MEDIUM_DESKTOP_BREAKPOINT}px;
  padding: 0 ${(p) => p.theme.spacing.xxl} ${(p) => p.theme.spacing.xl};
  box-sizing: border-box;
  text-align: left;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 0 ${(p) => p.theme.spacing.l};
  }
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: ${(p) => p.theme.spacing.l};
  row-gap: ${(p) => p.theme.spacing.l};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: 1fr;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  column-gap: ${(p) => p.theme.spacing.l};
  row-gap: ${(p) => p.theme.spacing.l};
  margin-bottom: ${(p) => p.theme.spacing.l};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: 1fr;
  }
`;

const PostsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${(p) => p.theme.spacing.l};
  row-gap: ${(p) => p.theme.spacing.xl};
  margin-bottom: ${(p) => p.theme.spacing.xl};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: ${(p) => p.theme.spacing.s};
  }

  @media (min-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: 1fr 1fr 1fr;
    gap: ${(p) => p.theme.spacing.m};
  }

  @media (min-width: ${SMALL_DESKTOP_BREAKPOINT}px) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: ${(p) => p.theme.spacing.l};
  }

  @media (min-width: ${MEDIUM_DESKTOP_BREAKPOINT}px) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: ${(p) => p.theme.spacing.l};
  }

  @media (min-width: ${LARGE_DESKTOP_BREAKPOINT}px) {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
    gap: ${(p) => p.theme.spacing.l};
  }
`;

const HeaderWrap = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  margin: 0 0 ${(p) => p.theme.spacing.xl};
  position: relative;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${(p) => p.theme.spacing.m};
    margin-bottom: ${(p) => p.theme.spacing.l};
  }
`;

const FilterWrap = styled(Flex)`
  display: flex;
  flex-direction: row;
  gap: ${(p) => p.theme.spacing.s};
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
`;

export interface FilterOption {
  label: string;
  id: string | BookingPostType;
}

export const content: FilterOption[] = [
  { id: BookingPostType.BookingPostTypeFeed, label: "Instagram Posts" },
  { id: BookingPostType.BookingPostTypeReel, label: "Instagram Reels" },
  { id: BookingPostType.BookingPostTypeStory, label: "Instagram Stories" },
  { id: BookingPostType.BookingPostTypeTiktok, label: "TikTok" },
];

const sortOptions = [
  {
    label: "Views",
    value: BookingPostsSortBy.BookingPostsSortByImpressions,
  },
  {
    label: "Reach",
    value: BookingPostsSortBy.BookingPostsSortByReach,
  },
  {
    label: "Engagement",
    value: BookingPostsSortBy.BookingPostsSortByEngagement,
  },
];

export interface MatchParams {
  page: string;
}

// Helper functions to serialize/deserialize URL params
const serializeFilters = (
  startDate: Date | null,
  endDate: Date | null,
  selectedListings: string[],
  selectedLocations: string[],
  selectedContentType: BookingPostType[],
  allListings: FilterOption[],
  allLocations: FilterOption[]
) => {
  const params = new URLSearchParams();

  // Only add to URL if filters are actually applied (not default "all")
  if (startDate) params.set("startDate", startDate.toISOString());
  if (endDate) params.set("endDate", endDate.toISOString());

  // Only add listings if not all are selected
  const allListingIds = allListings.map((l) => l.id);
  const hasAllListings =
    allListingIds.length > 0 &&
    allListingIds.length === selectedListings.length &&
    allListingIds.every((id) => selectedListings.includes(id));
  if (!hasAllListings && selectedListings.length > 0) {
    params.set("listings", selectedListings.join(","));
  }

  // Only add locations if not all are selected
  const allLocationIds = allLocations.map((l) => l.id);
  const hasAllLocations =
    allLocationIds.length > 0 &&
    allLocationIds.length === selectedLocations.length &&
    allLocationIds.every((id) => selectedLocations.includes(id));
  if (!hasAllLocations && selectedLocations.length > 0) {
    params.set("locations", selectedLocations.join(","));
  }

  // Only add contentTypes if not all are selected
  const allContentTypes = content.map((c) => c.id as BookingPostType);
  const hasAllContentTypes = allContentTypes.every((type) =>
    selectedContentType.includes(type)
  );
  if (!hasAllContentTypes && selectedContentType.length > 0) {
    params.set("contentTypes", selectedContentType.join(","));
  }

  return params.toString();
};

const deserializeFilters = (search: string) => {
  const params = new URLSearchParams(search);

  return {
    startDate: params.get("startDate")
      ? new Date(params.get("startDate")!)
      : null,
    endDate: params.get("endDate") ? new Date(params.get("endDate")!) : null,
    selectedListings: params.get("listings")?.split(",").filter(Boolean) || [],
    selectedLocations:
      params.get("locations")?.split(",").filter(Boolean) || [],
    selectedContentType: params.get("contentTypes")
      ? (params.get("contentTypes")!.split(",") as BookingPostType[])
      : content.map((c) => c.id as BookingPostType),
  };
};

export const BrandReports = () => {
  const history = useHistory();
  const location = useLocation();

  // Initialize from URL params
  const initialFilters = useMemo(
    () => deserializeFilters(location.search),
    [location.search]
  );

  const [startDate, setStartDate] = useState<Date | null>(
    initialFilters.startDate
  );
  const [endDate, setEndDate] = useState<Date | null>(initialFilters.endDate);
  const [csvUrl, setCsvUrl] = useState<string | null>(null);
  const window = useWindowSize();
  const brand = useSelector(authSelectors.activeBrand);
  const activeBrandId = useSelector(authSelectors.activeBrandId);
  const activeGroupId = useSelector(authSelectors.activeGroupId);
  const minDate = brand?.createdAt
    ? fromUnixTime(brand.createdAt)
    : new Date(0);
  const handleDateChange = ([newStartDate, newEndDate]: [
    Date | null,
    Date | null
  ]) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };
  const [listings, setListings] = useState<FilterOption[]>([]);
  const [locations, setLocations] = useState<FilterOption[]>([]);
  const [selectedListings, setSelectedListings] = useState<string[]>(
    initialFilters.selectedListings
  );
  const [selectedLocations, setSelectedLocations] = useState<string[]>(
    initialFilters.selectedLocations
  );
  const [selectedContentType, setSelectedContentType] = useState<
    BookingPostType[]
  >(initialFilters.selectedContentType);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const client = useGqlClient();
  const { data: listingsData } = useBrandListingsQuery(
    client,
    { brandId: activeBrandId ?? "" },
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: activeBrandId !== "",
    }
  );

  const { data: locationsData } = useBrandLocationsQuery(
    client,
    { brandId: activeBrandId ?? "" },
    {
      enabled: activeBrandId !== "",
      refetchOnWindowFocus: false,
    }
  );

  const { data: groupLocationsData } = useBrandGroupLocationsQuery(
    client,
    { brandGroupId: activeGroupId ?? "" },
    {
      enabled: activeGroupId !== "",
      refetchOnWindowFocus: false,
    }
  );

  const { data: groupListingsData } = useBrandGroupListingsQuery(
    client,
    { brandGroupId: activeGroupId ?? "" },
    {
      enabled: activeGroupId !== "",
      refetchOnWindowFocus: false,
    }
  );

  const transformDataToFilterOptions = useCallback(
    (data: Array<Record<string, any>>, key: string) =>
      data.map((item) => ({ id: item.id, label: item[key] })),
    []
  );

  // Memoize transformed data
  const transformedListings = useMemo(() => {
    const rawListings =
      listingsData?.listings.listings ||
      groupListingsData?.brandGroup?.listings ||
      [];
    return transformDataToFilterOptions(rawListings, "name");
  }, [listingsData, groupListingsData, transformDataToFilterOptions]);

  const transformedLocations = useMemo(() => {
    const rawLocations =
      locationsData?.brand?.locations.locations ||
      groupLocationsData?.brandGroup?.locations ||
      [];
    return transformDataToFilterOptions(rawLocations, "name");
  }, [locationsData, groupLocationsData, transformDataToFilterOptions]);

  // Optimize content types - distinguish between all selected (undefined) vs none selected ([])
  const optimizedContentTypes = useMemo(() => {
    const allContentTypes = content.map((c) => c.id as BookingPostType);

    // None selected - send empty array for IS NULL filter
    if (selectedContentType.length === 0) return [];

    // All selected - send undefined for no filter
    const hasAllSelected = allContentTypes.every((type) =>
      selectedContentType.includes(type)
    );
    return hasAllSelected ? undefined : selectedContentType;
  }, [selectedContentType]);

  // Optimize listings - distinguish between all selected (undefined) vs none selected ([])
  const optimizedListings = useMemo(() => {
    if (listings.length === 0) return undefined; // No data loaded yet, don't filter

    const allListingIds = listings.map((listing) => listing.id);

    // None selected - send empty array for IS NULL filter
    if (selectedListings.length === 0) return [];

    // All selected - send undefined for no filter
    const hasAllSelected =
      allListingIds.length > 0 &&
      allListingIds.every((id) => selectedListings.includes(id));
    return hasAllSelected ? undefined : selectedListings;
  }, [listings, selectedListings]);

  // Optimize locations - distinguish between all selected (undefined) vs none selected ([])
  const optimizedLocations = useMemo(() => {
    if (locations.length === 0) return undefined; // No data loaded yet, don't filter

    const allLocationIds = locations.map((location) => location.id);

    // None selected - send empty array for IS NULL filter
    if (selectedLocations.length === 0) return [];

    // All selected - send undefined for no filter
    const hasAllSelected =
      allLocationIds.length > 0 &&
      allLocationIds.every((id) => selectedLocations.includes(id));
    return hasAllSelected ? undefined : selectedLocations;
  }, [locations, selectedLocations]);

  // Update state when data changes
  useEffect(() => {
    setListings(transformedListings);
    // Only set all listings if none are selected (no URL params)
    if (initialFilters.selectedListings.length === 0) {
      setSelectedListings(transformedListings.map((listing) => listing.id));
    } else {
      // Validate URL params against loaded data
      const validListingIds = transformedListings.map((l) => l.id);
      const validSelected = initialFilters.selectedListings.filter((id) =>
        validListingIds.includes(id)
      );
      setSelectedListings(
        validSelected.length > 0 ? validSelected : validListingIds
      );
    }
  }, [transformedListings, initialFilters.selectedListings]);

  useEffect(() => {
    setLocations(transformedLocations);
    // Only set all locations if none are selected (no URL params)
    if (initialFilters.selectedLocations.length === 0) {
      setSelectedLocations(transformedLocations.map((location) => location.id));
    } else {
      // Validate URL params against loaded data
      const validLocationIds = transformedLocations.map((l) => l.id);
      const validSelected = initialFilters.selectedLocations.filter((id) =>
        validLocationIds.includes(id)
      );
      setSelectedLocations(
        validSelected.length > 0 ? validSelected : validLocationIds
      );
    }
  }, [transformedLocations, initialFilters.selectedLocations]);

  // Update URL when filters change
  useEffect(() => {
    const queryString = serializeFilters(
      startDate,
      endDate,
      selectedListings,
      selectedLocations,
      selectedContentType,
      listings,
      locations
    );

    const newUrl = queryString
      ? `${location.pathname}?${queryString}`
      : location.pathname;

    if (newUrl !== `${location.pathname}${location.search}`) {
      history.replace(newUrl);
    }
  }, [
    startDate,
    endDate,
    selectedListings,
    selectedLocations,
    selectedContentType,
    listings,
    locations,
    history,
    location.pathname,
    location.search,
  ]);

  return (
    <Wrap>
      <HeaderWrap>
        <H1 margin="0 l 0 0">Reports</H1>
        <FilterWrap>
          <DateRangePicker
            initialStartDate={startDate}
            initialEndDate={endDate}
            onChange={handleDateChange}
            tooltip="Based on the application date"
            onOpenChange={setDatePickerOpen}
            open={datePickerOpen}
          />
          <Dropdown
            options={listings.map((l) => ({ label: l.label, value: l.id }))}
            selectedOptions={selectedListings}
            setSelectedOptions={setSelectedListings}
            renderLabel={() => {
              if (selectedListings.length === 0) return "No Campaigns";
              if (selectedListings.length === listings.length)
                return "All Campaigns";
              if (selectedListings.length === 1) {
                const selected = listings.find(
                  (l) => l.id === selectedListings[0]
                );
                return selected?.label || "Campaign";
              }
              return `${selectedListings.length} Campaigns`;
            }}
            selectionMode="multiple"
            size="xs"
            showSelectAll
            anchor="bottom end"
            icon={<ListingsIcon colorPreset="secondary" width={17} />}
            onDropdownClick={() => {
              if (datePickerOpen) setDatePickerOpen(false);
            }}
          />
          <Dropdown
            options={locations.map((l) => ({ label: l.label, value: l.id }))}
            selectedOptions={selectedLocations}
            setSelectedOptions={setSelectedLocations}
            renderLabel={() => {
              if (selectedLocations.length === 0) return "No Locations";
              if (selectedLocations.length === locations.length)
                return "All Locations";
              if (selectedLocations.length === 1) {
                const selected = locations.find(
                  (l) => l.id === selectedLocations[0]
                );
                return selected?.label || "Location";
              }
              return `${selectedLocations.length} Locations`;
            }}
            selectionMode="multiple"
            size="xs"
            showSelectAll
            anchor="bottom end"
            icon={<LocationsIcon colorPreset="secondary" width={17} />}
            onDropdownClick={() => {
              if (datePickerOpen) setDatePickerOpen(false);
            }}
          />
          <Dropdown
            options={content.map((c) => ({ label: c.label, value: c.id }))}
            selectedOptions={selectedContentType}
            setSelectedOptions={(selected) =>
              setSelectedContentType(selected as BookingPostType[])
            }
            renderLabel={() => {
              if (selectedContentType.length === 0) return "No Content Types";
              if (selectedContentType.length === content.length)
                return "All Content";
              if (selectedContentType.length === 1) {
                const selected = content.find(
                  (c) => c.id === selectedContentType[0]
                );
                return selected?.label || "Content Type";
              }
              return `${selectedContentType.length} Content Types`;
            }}
            selectionMode="multiple"
            size="xs"
            showSelectAll
            anchor="bottom end"
            icon={<ContentIcon colorPreset="text" width={17} />}
            onDropdownClick={() => {
              if (datePickerOpen) setDatePickerOpen(false);
            }}
          />
          {csvUrl &&
          window &&
          window.width &&
          window.width > MOBILE_BREAKPOINT ? (
            <a
              href={csvUrl}
              download={`joli-csv-export-${format(
                startDate ? startDate : minDate,
                "dd/MM/yyy"
              )}-to-${format(endDate ? endDate : new Date(), "dd/MM/yyy")}`}
            >
              <FakeInput>
                <Flex
                  align="center"
                  justify="center"
                  style={{ height: 36, width: 36 }}
                >
                  <DownloadIcon
                    colorPreset="secondary"
                    width={15}
                    height={16}
                  />
                </Flex>
              </FakeInput>
            </a>
          ) : null}
        </FilterWrap>
      </HeaderWrap>

      <Reports
        startDate={startDate ? startDate : minDate}
        endDate={endDate}
        setCsvUrl={setCsvUrl}
        selectedListings={optimizedListings}
        selectedLocations={optimizedLocations}
        selectedContentType={optimizedContentTypes}
      />
    </Wrap>
  );
};

interface ReportsProps {
  startDate: Date | null;
  endDate: Date | null;
  setCsvUrl: (url: string | null) => void;
  selectedListings: string[] | undefined;
  selectedLocations: string[] | undefined;
  selectedContentType: BookingPostType[] | undefined;
}

const formatDate = (timestamp: number): string => {
  const date = new Date(timestamp * 1000);
  return date.toISOString().split("T")[0];
};

const convertArrayToCSV = (
  array: ReportMetricsQuery["reportMetrics"]["metrics"]
): string => {
  const modifiedData = array.map(({ timestamp, ...rest }) => ({
    ...rest,
    date: formatDate(timestamp),
  }));

  const headers = Object.keys(modifiedData[0]).join(",");

  const rows = modifiedData.map((obj) => {
    return Object.values(obj)
      .map((value) => {
        if (value === undefined) {
          return "";
        } else {
          return `"${value.toString().replace(/"/g, '""')}"`;
        }
      })
      .join(",");
  });

  return [headers, ...rows].join("\r\n");
};

function Reports({
  startDate,
  endDate,
  selectedContentType,
  selectedListings,
  selectedLocations,
  setCsvUrl,
}: ReportsProps) {
  const client = useGqlClient();
  const activeGroupId = useSelector(authSelectors.activeGroupId);
  const activeBrandId = useSelector(authSelectors.activeBrandId);

  const [sortProperty, setSortProperty] = useState<BookingPostsSortBy>(
    BookingPostsSortBy.BookingPostsSortByImpressions
  );

  const queryVariables = {
    filters: {
      startDate: startDate ? getUnixTime(startDate) : undefined,
      endDate: endDate ? getUnixTime(endDate) : undefined,
      sortBy: sortProperty,
      listingIDs: selectedListings,
      locationIDs: selectedLocations,
      contentTypes: selectedContentType,
    },
  };

  const queryOptions = {
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    // Add activeGroupId to force separate cache entries for different groups as the other variables don't change and we had issues with stale data
    queryKeyHashFn: (queryKey: any) => {
      return JSON.stringify([...queryKey, activeGroupId]);
    },
  };

  const {
    data: bookingPostsData,
    isLoading: bookingPostsLoading,
    error: bookingPostsError,
  } = useReportBookingPostsQuery(client, queryVariables, queryOptions);
  const {
    data: bookingsMetricsData,
    isLoading: bookingsMetricsLoading,
    error: bookingsMetricsError,
  } = useBookingsMetricsQuery(client, queryVariables, queryOptions);
  const {
    data: reportMetricsData,
    isLoading: reportMetricsLoading,
    error: reportMetricsError,
  } = useReportMetricsQuery(client, queryVariables, queryOptions);

  const { data: socialData } = useGetBrandSocialsQuery(
    client,
    {
      id: activeBrandId ? activeBrandId : "",
    },
    {
      enabled: activeBrandId !== "",
    }
  );

  useEffect(() => {
    if (!reportMetricsData) {
      return;
    }

    if (reportMetricsData.reportMetrics.metrics.length === 0) {
      setCsvUrl(null);
    } else {
      const csvString = convertArrayToCSV(
        reportMetricsData.reportMetrics.metrics
      );
      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      setCsvUrl(url);
    }
  }, [reportMetricsData, setCsvUrl]);

  if (bookingPostsError || bookingsMetricsError || reportMetricsError) {
    return (
      <Flex align="center" justify="center" direction="column">
        <Text>Something went wrong</Text>
      </Flex>
    );
  }

  // Show loading only if all queries are loading
  // booking post is a bit slower so handle that later
  if (
    (bookingsMetricsLoading && reportMetricsLoading) ||
    !reportMetricsData ||
    !bookingsMetricsData
  ) {
    return <Loading />;
  }

  if (
    reportMetricsData?.reportMetrics.totalImpressions === 0 &&
    bookingsMetricsData?.bookingsMetrics.totalApplications === 0
  ) {
    return (
      <Flex
        style={{ marginTop: "120px" }}
        margin="0"
        direction="column"
        align="center"
      >
        <Text isCompact weight="semi" margin="0" colorPreset="secondary">
          No data for this date range
        </Text>
        {socialData &&
        socialData.brand &&
        !socialData.brand.instagram &&
        !socialData.brand.tikTok ? (
          <Flex margin="m 0 0">
            <CTA to="/b/settings" type="primary" size="xs">
              Connect your Socials
            </CTA>
          </Flex>
        ) : null}
      </Flex>
    );
  }

  return (
    <>
      <StatsGrid>
        <StatsCard
          key={"applications"}
          label="Applications"
          value={bookingsMetricsData?.bookingsMetrics.totalApplications || 0}
          breakdown={[
            {
              label: "Approved",
              value: bookingsMetricsData?.bookingsMetrics.totalBookings || 0,
            },
            {
              label: "Rejected",
              value:
                bookingsMetricsData?.bookingsMetrics.totalRejectedBookings || 0,
            },
            {
              label: "Cancelled",
              value:
                bookingsMetricsData?.bookingsMetrics.totalCancelledBookings ||
                0,
            },
            {
              label: "Approval",
              value:
                bookingsMetricsData &&
                bookingsMetricsData.bookingsMetrics.totalApplications > 0
                  ? Math.round(
                      (bookingsMetricsData.bookingsMetrics.totalBookings /
                        bookingsMetricsData.bookingsMetrics.totalApplications) *
                        100
                    )
                  : 0,
            },
          ]}
          // previousValue={1}
        />
        <StatsCard
          key={"bookings"}
          label="Bookings"
          breakdown={[
            {
              label: "Completed",
              value:
                bookingsMetricsData?.bookingsMetrics.totalCompletedBookings ||
                0,
            },
            {
              label: "Upcoming",
              value:
                bookingsMetricsData?.bookingsMetrics.totalUpcomingBookings || 0,
            },
            {
              label: "In Progress",
              value:
                bookingsMetricsData?.bookingsMetrics.totalOverdueBookings || 0,
            },
          ]}
          value={bookingsMetricsData?.bookingsMetrics.totalBookings || 0}
          // previousValue={1}
        />
        <StatsCard
          key={"content"}
          label="Content"
          breakdown={[
            {
              label: "Posts",
              value: bookingsMetricsData?.bookingsMetrics.totalPosts || 0,
            },
            {
              label: "Reels",
              value: bookingsMetricsData?.bookingsMetrics.totalReels || 0,
            },
            {
              label: "Stories",
              value: bookingsMetricsData?.bookingsMetrics.totalStories || 0,
            },
            {
              label: "TikTok",
              value: bookingsMetricsData?.bookingsMetrics.totalTiktoks || 0,
            },
          ]}
          value={
            (bookingsMetricsData?.bookingsMetrics.totalPosts || 0) +
            (bookingsMetricsData?.bookingsMetrics.totalReels || 0) +
            (bookingsMetricsData?.bookingsMetrics.totalStories || 0) +
            (bookingsMetricsData?.bookingsMetrics.totalTiktoks || 0)
          }
          // previousValue={1}
        />
        <StatsCard
          key={"engagement"}
          label="Engagement Rate"
          breakdown={[
            {
              label: "Likes",
              value: bookingsMetricsData?.bookingsMetrics.totalLikes || 0,
            },
            {
              label: "Comments",
              value: bookingsMetricsData?.bookingsMetrics.totalComments || 0,
            },
            {
              label: "Shares",
              value: bookingsMetricsData?.bookingsMetrics.totalShares || 0,
            },
            {
              label: "Saves",
              value: bookingsMetricsData?.bookingsMetrics.totalSaves || 0,
            },
          ]}
          value={
            reportMetricsData && reportMetricsData.reportMetrics.totalReach > 0
              ? Math.round(
                  (reportMetricsData.reportMetrics.totalEngagement /
                    reportMetricsData.reportMetrics.totalReach) *
                    100
                )
              : 0
          }
          // previousValue={99}
        />
      </StatsGrid>

      <ChartsGrid>
        <StatsCard
          key={"views"}
          label="Total Views" // string matched on StatsCard
          value={reportMetricsData?.reportMetrics.totalImpressions || 0}
          // previousValue={data.reportMetrics.prevTotalImpressions}
          metrics={[
            {
              label: "Current",
              data:
                reportMetricsData?.reportMetrics.metrics.map((rm) => {
                  return [rm.timestamp * 1000, rm.impressions];
                }) || [],
              lineColor: "primary",
            },
            {
              label: "Previous",
              data:
                reportMetricsData?.reportMetrics.trailingMetrics.map((rm) => {
                  return [rm.timestamp * 1000, rm.impressions];
                }) || [],
              lineColor: "secondary",
            },
          ]}
        />
        <StatsCard
          key={"engagements"}
          label="Total Engagements" // string matched on StatsCard
          value={reportMetricsData?.reportMetrics.totalEngagement || 0}
          // previousValue={data.reportMetrics.prevTotalEngagement}
          metrics={[
            {
              label: "Current",
              data:
                reportMetricsData?.reportMetrics.metrics.map((rm) => {
                  return [rm.timestamp * 1000, rm.engagement];
                }) || [],
              lineColor: "primary",
            },
            {
              label: "Previous",
              data:
                reportMetricsData?.reportMetrics.trailingMetrics.map((rm) => {
                  return [rm.timestamp * 1000, rm.engagement];
                }) || [],
              lineColor: "secondary",
            },
          ]}
        />
      </ChartsGrid>

      {/* Top Content section - shows as bookingPosts data becomes available */}
      {bookingPostsLoading ? (
        <>
          <Flex
            margin="m 0"
            direction="row"
            justify="space-between"
            align="baseline"
          >
            <H3 margin="xxl 0 m">Top Content</H3>
          </Flex>
          <Loading />
        </>
      ) : bookingPostsData?.bookingPosts.bookingPosts.length &&
        bookingPostsData.bookingPosts.bookingPosts.length > 0 ? (
        <>
          <Flex
            margin="m 0"
            direction="row"
            justify="space-between"
            align="baseline"
          >
            <H3 margin="xxl 0 m">Top Content</H3>
            <Dropdown
              selectedOptions={[
                BookingPostsSortBy.BookingPostsSortByImpressions,
              ]}
              setSelectedOptions={(selectedOptions) => {
                const selectedValue = selectedOptions[0] as BookingPostsSortBy;
                setSortProperty(selectedValue);
              }}
              renderLabel={() => {
                return `Sort by ${
                  sortOptions.filter((e) => e.value === sortProperty)[0].label
                }`;
              }}
              selectionMode="single"
              options={sortOptions}
              dropdownType="text"
            />
          </Flex>
          <PostsGrid>
            {bookingPostsData?.bookingPosts.bookingPosts
              .slice(0, 10)
              .map((bp) => {
                let value = 0;
                switch (sortProperty) {
                  case BookingPostsSortBy.BookingPostsSortByEngagement:
                    value = bp.engagement;
                    break;
                  case BookingPostsSortBy.BookingPostsSortByReach:
                    value = bp.reach;
                    break;
                  case BookingPostsSortBy.BookingPostsSortByImpressions:
                    value = bp.impressions;
                    break;
                  default:
                    value = bp.impressions;
                }

                return (
                  <BookingPost
                    key={bp.id}
                    name={
                      bp.postType === BookingPostType.BookingPostTypeTiktok &&
                      bp.creator.tikTok
                        ? bp.creator.tikTok.displayName
                        : bp.creator.instagram
                        ? bp.creator.instagram.name
                        : bp.creator.preferredProfile
                        ? bp.creator.preferredProfile.name
                        : ""
                    }
                    username={
                      bp.postType === BookingPostType.BookingPostTypeTiktok &&
                      bp.creator.tikTok
                        ? bp.creator.tikTok.username
                        : bp.creator.instagram
                        ? bp.creator.instagram.username
                        : bp.creator.preferredProfile
                        ? bp.creator.preferredProfile.username
                        : ""
                    }
                    mediaUrl={bp.mediaUrl}
                    metricValue={value}
                    caption={bp.mediaCaption}
                    mediaType={bp.mediaType}
                    thumbnailUrl={bp.thumbnailUrl}
                    postType={bp.postType}
                    id={bp.id}
                    square={true}
                    showCreator={true}
                    maxWidth={500}
                  />
                );
              })}
          </PostsGrid>
          <Flex justify="center" margin="0 0 0">
            {bookingPostsData?.bookingPosts.bookingPosts &&
              bookingPostsData.bookingPosts.bookingPosts.length >= 10 && (
                <TextLink margin="0 0 xxl" to="/b/content">
                  See more
                </TextLink>
              )}
          </Flex>
        </>
      ) : null}
    </>
  );
}
